import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Pencil, Eye, EyeOff, Bell, Users, Globe,
  Calendar, File, Folder, FileText, Link, Trophy, Clock, CalendarX, FileUser, Award,
  type LucideIcon, FolderInput, ScanHeart,
} from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Badge } from '@/components/shadcn/badge'
import { ServiceModuleType, PermissionEntityType, type ServiceModuleModel } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { ICON_BY_NAME } from "@/components/common/iconByName.tsx";

/**
 * Types selectable in the create dialog (everything except Undefined). `Icon` is the
 * fallback per type when the module has no (resolvable) `iconName`.
 * Labels come from the `service-modules` NS: `t(\`types.\${value}\`)`.
 */
export const SERVICE_MODULE_TYPES: { value: ServiceModuleType; Icon: LucideIcon, defaultColor: string}[] = [
  { value: ServiceModuleType.Calendar, Icon: Calendar, defaultColor: '#4ca7aa'},
  { value: ServiceModuleType.File, Icon: File, defaultColor: '#6366f1' },
  { value: ServiceModuleType.Folder, Icon: Folder, defaultColor: '#f59e0b' },
  { value: ServiceModuleType.Text, Icon: FileText, defaultColor: '#10b981' },
  { value: ServiceModuleType.Url, Icon: Link, defaultColor: '#3b82f6' },
  { value: ServiceModuleType.Leaderboard, Icon: Trophy, defaultColor: '#f59e0b' },
  { value: ServiceModuleType.Timetracking, Icon: Clock, defaultColor: '#8b5cf6' },
  { value: ServiceModuleType.Absence, Icon: CalendarX, defaultColor: '#ef4444'},
  { value: ServiceModuleType.PersonalDocuments, Icon: FileUser, defaultColor: '#64748b' },
  { value: ServiceModuleType.StatusSystem, Icon: Award, defaultColor: '#eab308' },
  { value: ServiceModuleType.Insurance, Icon: ScanHeart, defaultColor: '#c41212' },
]

interface Props {
  module: ServiceModuleModel
  onEdit: (module: ServiceModuleModel) => void
  /** Card click = open the module (type-dependent view). */
  onOpen: (module: ServiceModuleModel) => void
  onMove: (module: ServiceModuleModel) => void
  /** Optional drag handle (only when the level is sortable via D&D). */
  dragHandle?: ReactNode
}

/** Tile of a service module — display only, actions via callback. */
export function ServiceModuleCard({ module, onEdit, onOpen, onMove, dragHandle }: Props) {
  const { t } = useTranslation('service-modules')
  const { userHasEntityPermission } = usePermissions()
  const canEdit = module.id != null && userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', module.id)
  const color = module.colorHex || '#64748b'
  const groupCount = module.assignedGroupIds?.length ?? 0

  const typeEntry = SERVICE_MODULE_TYPES.find((e) => e.value === module.type)
  // iconName first (curated backend map), otherwise type fallback, otherwise neutral.
  const iconFromName = module.iconName ? ICON_BY_NAME[module.iconName.trim().toLowerCase()] : undefined
  const Icon = iconFromName ?? typeEntry?.Icon ?? File

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(module)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(module)
        }
      }}
      className="flex flex-col gap-0 border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-card cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Header (analogous to ActionCard): icon left, name, edit button — below it
          the border-b as separator. Icon = uploaded image OR the icon mapped via
          iconName/type in the module color. */}
      <div className="h-14 flex items-center gap-3 px-4 border-b">
        <div className="flex size-8 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-muted/40">
          {module.iconUrl ? (
            <img src={module.iconUrl} alt="" className="size-full object-cover" />
          ) : (
            <Icon className="size-6" style={{ color }} />
          )}
        </div>
        <p className="font-semibold text-sm flex-1 truncate">{module.name || '—'}</p>
        {canEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(module)
            }}
          >
            <Pencil className="size-3.5" />
          </Button>
        )}
        {onMove && (
            <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={(e) => {
                  e.stopPropagation()
                  onMove(module)
                }}
            >
              <FolderInput className="size-3.5" />
            </Button>
        )}
        {dragHandle}
      </div>

      {/* Inhalt */}
      <div className="flex flex-col gap-2 p-3">
        <p className="text-xs text-muted-foreground">{typeEntry ? t(`types.${typeEntry.value}`) : ''}</p>

        {/* Status-Badges */}
        <div className="flex flex-wrap gap-1">
          <Badge variant={module.isVisible ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0 gap-1">
            {module.isVisible ? <Eye className="size-2.5" /> : <EyeOff className="size-2.5" />}
            {module.isVisible ? t('card.visible') : t('card.hidden')}
          </Badge>
          {module.sendNotification && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
              <Bell className="size-2.5" />
              {t('card.notification')}
            </Badge>
          )}
          {module.isCompanyWideAssigned ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
              <Globe className="size-2.5" />
              {t('card.all')}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 gap-1">
              <Users className="size-2.5" />
              {t('card.groups', { count: groupCount })}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}
